/**
 * Shapes returned by the `writer_telemetry_overview` RPC plus the parsing
 * helpers that bridge the ledger's metadata contract.
 *
 * The agent writes `usage / tool_calls / tool_names / model`; earlier drafts of
 * the telemetry page assumed `token_usage / tool_call_count / tools_used /
 * model_id`. `parseTurnMetadata` accepts both so historical rows and any future
 * rename keep parsing.
 */
import { addTokens, costEur, EMPTY_TOKENS, type TokenBreakdown } from './telemetryCost';

export interface TurnMetadata extends TokenBreakdown {
  model: string;
  iterations: number;
  stopReason: string;
  readOnlyToolCalls: number;
  toolCalls: number;
  durationMs: number;
  toolNames: string[];
}

function num(x: unknown, fallback = 0): number {
  if (typeof x === 'number' && Number.isFinite(x)) return x;
  if (typeof x === 'string' && x.trim() !== '' && Number.isFinite(Number(x))) return Number(x);
  return fallback;
}

function firstNum(...vals: unknown[]): number {
  for (const v of vals) {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  }
  return 0;
}

export function parseTurnMetadata(metadata: unknown): TurnMetadata {
  const m = (metadata ?? {}) as Record<string, unknown>;
  const usage = ((m.usage ?? m.token_usage) ?? {}) as Record<string, unknown>;
  const rawTools = Array.isArray(m.tool_names)
    ? m.tool_names
    : Array.isArray(m.tools_used)
      ? m.tools_used
      : [];
  const model =
    (typeof m.model === 'string' && m.model) ||
    (typeof m.model_id === 'string' && m.model_id) ||
    'unknown';

  const tokensIn = firstNum(usage.promptTokenCount, usage.prompt_tokens, usage.input_tokens);
  const tokensCached = firstNum(usage.cachedContentTokenCount, usage.cached_tokens);
  const tokensThinking = firstNum(usage.thoughtsTokenCount, usage.thinking_tokens);
  const tokensOut = firstNum(usage.candidatesTokenCount, usage.completion_tokens, usage.output_tokens);
  const tokensTotal = firstNum(usage.totalTokenCount, usage.total_tokens) ||
    tokensIn + tokensThinking + tokensOut;

  return {
    model,
    iterations: firstNum(m.iterations),
    stopReason:
      typeof m.stop_reason === 'string' && m.stop_reason ? m.stop_reason : 'unknown',
    readOnlyToolCalls: firstNum(m.read_only_tool_calls),
    toolCalls: firstNum(m.tool_calls, m.tool_call_count),
    durationMs: firstNum(m.duration_ms),
    toolNames: rawTools.filter((t): t is string => typeof t === 'string'),
    tokensIn,
    tokensCached,
    tokensThinking,
    tokensOut,
    tokensTotal,
  };
}

/* ------------------------------------------------------------------ RPC types */

export interface DailyModelRow {
  day: string;
  model: string;
  turns: number;
  iterations: number;
  tool_calls: number;
  duration_ms: number;
  tokens_in: number;
  tokens_cached: number;
  tokens_thinking: number;
  tokens_out: number;
  tokens_total: number;
  active_users: number;
}

export interface DailyCompileRow {
  day: string;
  success: number;
  failed: number;
  other: number;
}

export interface DailyJobRow {
  day: string;
  jobs: number;
  success: number;
  failed: number;
  p50_ms: number | null;
  p90_ms: number | null;
}

export interface ToolRow {
  tool: string;
  count: number;
  turns: number;
}

export interface CodeCount {
  code: string;
  count: number;
}

export interface UserRow {
  user_id: string;
  user_name: string | null;
  turns: number;
  tokens_total: number;
  tokens_in: number;
  tokens_cached: number;
  tokens_thinking: number;
  tokens_out: number;
  tool_calls: number;
  last_active: string;
  compiles: number;
  compiles_success: number;
}

export interface ProjectRow {
  project_id: string;
  project_title: string | null;
  owner_id: string | null;
  turns: number;
  tokens_total: number;
  tool_calls: number;
  last_active: string;
  compiles: number;
  compiles_success: number;
}

export interface DailyStopRow {
  day: string;
  turns: number;
  /** Turns that exhausted the iteration budget (incl. rescue outcomes). */
  cap_hits: number;
  /** Cap hits where the forced-write rescue landed the change. */
  rescued: number;
  /** Cap hits where even the rescue produced nothing. */
  rescue_failed: number;
  read_only_tool_calls: number;
}

export interface StopSummary {
  turns: number;
  capHits: number;
  rescued: number;
  rescueFailed: number;
  capHitRate: number | null;
  readOnlyToolCalls: number;
}

export function summarizeStops(rows: DailyStopRow[]): StopSummary {
  const s: StopSummary = {
    turns: 0,
    capHits: 0,
    rescued: 0,
    rescueFailed: 0,
    capHitRate: null,
    readOnlyToolCalls: 0,
  };
  for (const r of rows) {
    s.turns += Number(r.turns) || 0;
    s.capHits += Number(r.cap_hits) || 0;
    s.rescued += Number(r.rescued) || 0;
    s.rescueFailed += Number(r.rescue_failed) || 0;
    s.readOnlyToolCalls += Number(r.read_only_tool_calls) || 0;
  }
  s.capHitRate = s.turns > 0 ? s.capHits / s.turns : null;
  return s;
}

export interface RecentTurnRow {
  id: string;
  created_at: string;
  owner_id: string;
  owner_name: string | null;
  project_id: string | null;
  project_title: string | null;
  model: string;
  iterations: number;
  max_iterations?: number;
  stop_reason?: string;
  turn_goal?: string;
  tool_calls: number;
  read_only_tool_calls?: number;
  duration_ms: number;
  tool_names: string[];
  tokens_in: number;
  tokens_cached: number;
  tokens_thinking: number;
  tokens_out: number;
  tokens_total: number;
}

export interface RepairSummaryRow {
  attempts: number;
  layer0_attempts: number;
  layer1_attempts: number;
  layer1_applied: number;
  layer1_tokens_in: number;
  layer1_tokens_out: number;
}

export interface QueueRow {
  queued: number;
  running: number;
  failed: number;
  oldest_pending_at: string | null;
}

export interface TelemetryOverview {
  range: { from: string; to: string };
  daily_model: DailyModelRow[];
  daily_stops: DailyStopRow[];
  stop_reasons: CodeCount[];
  daily_compiles: DailyCompileRow[];
  daily_jobs: DailyJobRow[];
  tools: ToolRow[];
  failure_codes: CodeCount[];
  users: UserRow[];
  projects: ProjectRow[];
  repairs: RepairSummaryRow | null;
  repair_unresolved: CodeCount[];
  recent_turns: RecentTurnRow[];
  queue: QueueRow | null;
}

export const EMPTY_OVERVIEW: TelemetryOverview = {
  range: { from: '', to: '' },
  daily_model: [],
  daily_stops: [],
  stop_reasons: [],
  daily_compiles: [],
  daily_jobs: [],
  tools: [],
  failure_codes: [],
  users: [],
  projects: [],
  repairs: null,
  repair_unresolved: [],
  recent_turns: [],
  queue: null,
};

/* ------------------------------------------------------------------ derived */

export interface DaySeriesPoint {
  day: string;
  turns: number;
  iterations: number;
  toolCalls: number;
  durationMs: number;
  tokens: TokenBreakdown;
  costEur: number;
  /** True when at least one model in the day has no price entry. */
  costPartial: boolean;
  compilesSuccess: number;
  compilesFailed: number;
  compilesOther: number;
  compileSuccessRate: number | null;
  jobP50Ms: number | null;
  jobP90Ms: number | null;
  activeUsers: number;
  /** Per-model cost for the stacked chart. */
  costByModel: Record<string, number>;
}

function toTokens(r: DailyModelRow): TokenBreakdown {
  return {
    tokensIn: r.tokens_in,
    tokensCached: r.tokens_cached,
    tokensThinking: r.tokens_thinking,
    tokensOut: r.tokens_out,
    tokensTotal: r.tokens_total,
  };
}

/**
 * Folds the RPC's per-model daily rows, compile counts and job latencies into a
 * single, chart-ready series covering every day in the range (gaps included as
 * zero rows so the charts don't lie about activity).
 */
export function buildDaySeries(
  overview: TelemetryOverview,
  from: Date,
  to: Date,
): DaySeriesPoint[] {
  const byDay = new Map<string, DaySeriesPoint>();

  const dayKey = (d: Date) =>
    `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(
      d.getUTCDate(),
    ).padStart(2, '0')}`;

  const blank = (day: string): DaySeriesPoint => ({
    day,
    turns: 0,
    iterations: 0,
    toolCalls: 0,
    durationMs: 0,
    tokens: { ...EMPTY_TOKENS },
    costEur: 0,
    costPartial: false,
    compilesSuccess: 0,
    compilesFailed: 0,
    compilesOther: 0,
    compileSuccessRate: null,
    jobP50Ms: null,
    jobP90Ms: null,
    activeUsers: 0,
    costByModel: {},
  });

  const cursor = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate()));
  const end = new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate()));
  while (cursor <= end) {
    const k = dayKey(cursor);
    byDay.set(k, blank(k));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  const ensure = (day: string) => {
    let p = byDay.get(day);
    if (!p) {
      p = blank(day);
      byDay.set(day, p);
    }
    return p;
  };

  for (const r of overview.daily_model) {
    const p = ensure(r.day);
    p.turns += r.turns;
    p.iterations += r.iterations;
    p.toolCalls += r.tool_calls;
    p.durationMs += r.duration_ms;
    p.tokens = addTokens(p.tokens, toTokens(r));
    p.activeUsers = Math.max(p.activeUsers, r.active_users);
    const c = costEur(r.model, toTokens(r));
    if (c === null) p.costPartial = true;
    else {
      p.costEur += c;
      p.costByModel[r.model] = (p.costByModel[r.model] ?? 0) + c;
    }
  }

  for (const r of overview.daily_compiles) {
    const p = ensure(r.day);
    p.compilesSuccess += r.success;
    p.compilesFailed += r.failed;
    p.compilesOther += r.other;
  }

  for (const r of overview.daily_jobs) {
    const p = ensure(r.day);
    p.jobP50Ms = r.p50_ms;
    p.jobP90Ms = r.p90_ms;
  }

  for (const p of byDay.values()) {
    const done = p.compilesSuccess + p.compilesFailed;
    p.compileSuccessRate = done > 0 ? p.compilesSuccess / done : null;
  }

  return [...byDay.values()].sort((a, b) => a.day.localeCompare(b.day));
}

export interface RangeTotals {
  turns: number;
  toolCalls: number;
  iterations: number;
  durationMs: number;
  tokens: TokenBreakdown;
  costEur: number;
  costPartial: boolean;
  compiles: number;
  compilesSuccess: number;
  compilesFailed: number;
  compileSuccessRate: number | null;
  activeUsers: number;
  days: number;
  costPerTurn: number | null;
  toolCallsPerTurn: number;
  iterationsPerTurn: number;
  secondsPerTurn: number;
}

export function totalsFor(series: DaySeriesPoint[], activeUsers: number): RangeTotals {
  const t: RangeTotals = {
    turns: 0,
    toolCalls: 0,
    iterations: 0,
    durationMs: 0,
    tokens: { ...EMPTY_TOKENS },
    costEur: 0,
    costPartial: false,
    compiles: 0,
    compilesSuccess: 0,
    compilesFailed: 0,
    compileSuccessRate: null,
    activeUsers,
    days: series.length || 1,
    costPerTurn: null,
    toolCallsPerTurn: 0,
    iterationsPerTurn: 0,
    secondsPerTurn: 0,
  };
  for (const p of series) {
    t.turns += p.turns;
    t.toolCalls += p.toolCalls;
    t.iterations += p.iterations;
    t.durationMs += p.durationMs;
    t.tokens = addTokens(t.tokens, p.tokens);
    t.costEur += p.costEur;
    t.costPartial = t.costPartial || p.costPartial;
    t.compilesSuccess += p.compilesSuccess;
    t.compilesFailed += p.compilesFailed;
  }
  t.compiles = t.compilesSuccess + t.compilesFailed;
  t.compileSuccessRate = t.compiles > 0 ? t.compilesSuccess / t.compiles : null;
  t.costPerTurn = t.turns > 0 ? t.costEur / t.turns : null;
  t.toolCallsPerTurn = t.turns > 0 ? t.toolCalls / t.turns : 0;
  t.iterationsPerTurn = t.turns > 0 ? t.iterations / t.turns : 0;
  t.secondsPerTurn = t.turns > 0 ? t.durationMs / t.turns / 1000 : 0;
  return t;
}

/** Cost of a single recent-turn row, in EUR (null when the model is unpriced). */
export function turnCostEur(r: RecentTurnRow): number | null {
  return costEur(r.model, {
    tokensIn: r.tokens_in,
    tokensCached: r.tokens_cached,
    tokensThinking: r.tokens_thinking,
    tokensOut: r.tokens_out,
    tokensTotal: r.tokens_total,
  });
}

export function percentile(values: number[], p: number): number | null {
  const v = values.filter((x) => Number.isFinite(x)).sort((a, b) => a - b);
  if (v.length === 0) return null;
  const idx = Math.min(v.length - 1, Math.max(0, Math.ceil(p * v.length) - 1));
  return v[idx];
}

export type HealthTone = 'ok' | 'warn' | 'bad';

export function compileHealthTone(rate: number | null): HealthTone {
  if (rate === null) return 'ok';
  if (rate >= 0.85) return 'ok';
  if (rate >= 0.6) return 'warn';
  return 'bad';
}
